/**
 * Motifs de signalement proposés aux membres.
 * La liste est administrable (Administration > Sécurité > Modération) et
 * stockée dans `security_settings.content_moderation.report_reasons`.
 * Les valeurs ci-dessous servent uniquement de repli.
 */
export interface ReportReason {
  value: string;
  label: string;
}

export const DEFAULT_REPORT_REASONS: ReportReason[] = [
  { value: "violence", label: "Violence ou contenu dangereux" },
  { value: "hate", label: "Discours de haine ou discrimination" },
  { value: "harassment", label: "Harcèlement ou intimidation" },
  { value: "spam", label: "Spam ou contenu trompeur" },
  { value: "sexual", label: "Contenu sexuel inapproprié" },
  { value: "illegal", label: "Contenu illégal" },
  { value: "other", label: "Autre" },
];

export const REPORT_REASON_LABEL_MAX_LENGTH = 120;

/** Transforme un libellé en identifiant technique stable et lisible. */
export const slugifyReportReason = (label: string): string =>
  label
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);

/**
 * Nettoie la liste venant des réglages : libellés non vides, identifiants
 * uniques. Retourne les motifs par défaut si rien d'exploitable.
 */
export const normalizeReportReasons = (raw: unknown): ReportReason[] => {
  if (!Array.isArray(raw)) return DEFAULT_REPORT_REASONS;

  const seen = new Set<string>();
  const cleaned: ReportReason[] = [];

  raw.forEach((entry, index) => {
    if (!entry || typeof entry !== "object") return;
    const label = String((entry as ReportReason).label ?? "").trim();
    if (!label) return;
    let value = String((entry as ReportReason).value ?? "").trim() || slugifyReportReason(label);
    if (!value) value = `motif_${index + 1}`;
    while (seen.has(value)) value = `${value}_${index + 1}`;
    seen.add(value);
    cleaned.push({ value, label: label.slice(0, REPORT_REASON_LABEL_MAX_LENGTH) });
  });

  return cleaned.length > 0 ? cleaned : DEFAULT_REPORT_REASONS;
};
